/**
 * Tiny fetch wrapper with auth + refresh interceptor.
 *
 * - All requests to /api/v1/* get Authorization: Bearer <accessToken>
 * - On 401, the wrapper tries a single refresh + retry
 * - Throws a typed `ApiError` on non-2xx responses
 */

import {
  getAccessToken,
  getSessionGeneration,
  getStoredSession,
  hasSession,
  isCurrentSession,
  setSession,
  clearSession,
} from './auth'

const AUTH_BASE_URL = (import.meta.env?.VITE_AUTH_BASE_URL as string | undefined) ?? 'https://auth.thriv.es'
const FRIDAY_BASE_URL = '' // same-origin: /api/v1/* goes to Pages Functions

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message)
  }
}

export type IdempotencyKey = string

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  headers?: Record<string, string>
  signal?: AbortSignal
  idemKey?: IdempotencyKey
}

let inflightRefresh: Promise<string | true | null> | null = null

/**
 * Pages that are public, or that run their own sign-in flow. The bootstrap
 * `/me` call answers 401 for an anonymous visitor, and bouncing those pages
 * to /login would throw away the sign-in the user is in the middle of — or,
 * for the landing page, send the visitor away before they ever saw it.
 */
const AUTH_PAGE_PATHS = [
  '/',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/confirm-email',
  '/auth/google/callback',
]
function isAuthPage(pathname: string): boolean {
  return AUTH_PAGE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

async function refreshTokens(): Promise<string | true | null> {
  if (inflightRefresh) return inflightRefresh
  const session = getStoredSession()
  const generation = getSessionGeneration()
  // Without stored credentials, only the generation identifies the auth scope.
  const isCurrent = () =>
    session ? isCurrentSession(session, generation) : getSessionGeneration() === generation
  inflightRefresh = (async () => {
    try {
      if (!session || session.cookieSession) {
        const res = await fetch('/api/session/refresh', { method: 'POST' })
        // A logout or a different login can complete while refresh is pending.
        if (!isCurrent()) return null
        if (res.ok) return true
        if (res.status === 401) {
          clearSession()
          return null
        }
        throw new ApiError('AUTH_UNAVAILABLE', 'Session service is temporarily unavailable', res.status)
      }
      const res = await fetch(`${AUTH_BASE_URL}/api/auth/friday/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      })
      if (!res.ok) {
        if (isCurrent()) clearSession()
        return null
      }
      const json = (await res.json()) as { data?: { accessToken: string; refreshToken: string } }
      if (!json.data) {
        if (isCurrent()) clearSession()
        return null
      }
      // A logout or a different login can complete while refresh is pending.
      // Never install credentials obtained for the earlier auth scope.
      if (!isCurrent()) return null
      setSession({
        accessToken: json.data.accessToken,
        refreshToken: json.data.refreshToken,
        user: session.user,
      })
      return json.data.accessToken
    } catch (error) {
      if (!session || session.cookieSession) throw error
      if (isCurrent()) clearSession()
      return null
    } finally {
      inflightRefresh = null
    }
  })()
  return inflightRefresh
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const url = new URL(path.startsWith('http') ? path : `${FRIDAY_BASE_URL}${path}`, window.location.origin)
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v == null) continue
      url.searchParams.set(k, String(v))
    }
  }
  const headers: Record<string, string> = { ...(opts.headers ?? {}) }
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }
  if (opts.idemKey) headers['Idempotency-Key'] = opts.idemKey
  const access = getAccessToken()
  const authGeneration = getSessionGeneration()
  if (access) headers['Authorization'] = `Bearer ${access}`

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    signal: opts.signal,
  }
  if (opts.body !== undefined) {
    init.body =
      opts.body instanceof FormData
        ? opts.body
        : typeof opts.body === 'string'
          ? opts.body
          : JSON.stringify(opts.body)
  }

  let res = await fetch(url.toString(), init)

  // Refresh once on 401.
  if (
    res.status === 401 &&
    url.pathname.startsWith('/api/v1/') &&
    getSessionGeneration() === authGeneration
  ) {
    const fresh = await refreshTokens()
    if (fresh) {
      if (typeof fresh === 'string') headers['Authorization'] = `Bearer ${fresh}`
      res = await fetch(url.toString(), { ...init, headers })
    } else {
      // Refresh failed — bounce to login.
      if (
        !hasSession() &&
        typeof window !== 'undefined' &&
        !isAuthPage(location.pathname)
      ) {
        const next = encodeURIComponent(location.pathname + location.search)
        location.assign(`/login?next=${next}`)
      }
    }
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  let json: unknown
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    // Edge/proxy failures can be HTML. Never reflect that body into the UI.
    json = { error: { code: 'PARSE', message: 'The server returned an unexpected response. Please try again.' } }
  }
  if (!res.ok) {
    const err = (json as { error?: { code: string; message: string; details?: unknown } })?.error
    throw new ApiError(err?.code ?? 'ERROR', err?.message ?? res.statusText, res.status, err?.details)
  }
  // Unwrap the { data } envelope so callers get just the payload.
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data
  }
  return json as T
}

/**
 * Like `api()`, but returns the raw `Response` so the caller can read the
 * body as Blob/ArrayBuffer/stream (e.g. ZIP downloads). Still attaches the
 * Authorization header and retries once on 401 with a refreshed token.
 */
export async function apiRaw(path: string, opts: RequestOpts = {}): Promise<Response> {
  const url = new URL(path.startsWith('http') ? path : `${FRIDAY_BASE_URL}${path}`, window.location.origin)
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v == null) continue
      url.searchParams.set(k, String(v))
    }
  }
  const headers: Record<string, string> = { ...(opts.headers ?? {}) }
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }
  if (opts.idemKey) headers['Idempotency-Key'] = opts.idemKey
  const access = getAccessToken()
  const authGeneration = getSessionGeneration()
  if (access) headers['Authorization'] = `Bearer ${access}`

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    signal: opts.signal,
  }
  if (opts.body !== undefined) {
    init.body =
      opts.body instanceof FormData
        ? opts.body
        : typeof opts.body === 'string'
          ? opts.body
          : JSON.stringify(opts.body)
  }

  let res = await fetch(url.toString(), init)
  if (
    res.status === 401 &&
    url.pathname.startsWith('/api/v1/') &&
    getSessionGeneration() === authGeneration
  ) {
    const fresh = await refreshTokens()
    if (fresh) {
      if (typeof fresh === 'string') headers['Authorization'] = `Bearer ${fresh}`
      res = await fetch(url.toString(), { ...init, headers })
    } else {
      if (
        !hasSession() &&
        typeof window !== 'undefined' &&
        !isAuthPage(location.pathname)
      ) {
        const next = encodeURIComponent(location.pathname + location.search)
        location.assign(`/login?next=${next}`)
      }
    }
  }
  return res
}

/**
 * Helper for posting to auth.thriv.es directly. New sign-ins go through
 * Friday's own `/api/session/*` routes so the bearer tokens stay on the
 * server; this remains for callers that talk to the auth service directly.
 */
export async function authApi<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${AUTH_BASE_URL}${path}`, {
    method: body !== undefined ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    // The auth service answers with a flat envelope: { error: "<message>",
    // code: "<CODE>", statusCode }. Older builds nested the details instead.
    const json = (await res.json().catch(() => null)) as
      | { error?: { code?: string; message?: string } | string; code?: string; message?: string }
      | null
    const error = json?.error
    const code =
      (typeof error === 'object' && error?.code) || json?.code || 'AUTH_ERROR'
    const message =
      (typeof error === 'object' && error?.message) ||
      (typeof error === 'string' ? error : undefined) ||
      json?.message ||
      res.statusText
    throw new ApiError(code, message, res.status)
  }
  const json = (await res.json()) as { data: T }
  return json.data
}
